import _ from 'lodash';
import {useEffect} from 'react';

import {
  type Artifact,
  type ArtifactCollection,
  type ArtifactSequence,
  type FetchJobDetailsQueryHookResult,
  useFetchJobDetailsQuery,
} from '../../../generated/graphql';
import {type AccessOptions} from '../../../util/permissions';
import {ACTIVE_STATES} from '../constants';
import {getArtifactInfo, getVersionInfo} from '../Jobs/jobUtil';
import {convertArtifactToVersion, extractRuns} from './utilData';
import {type JobDetails, type JobRun, type JobRuns} from './utilTypes';

const JOB_DETAILS_POLL_INTERVAL_MS = 5000;

type JobDetailsQueryOptions = {
  defaultJobVersion?: string;
  usedByFilters?: string;
  usedByFirst?: number;
  includeRunArgs: boolean;
  pollInterval?: number;
};

type JobDetailsQueryControls = Pick<
  FetchJobDetailsQueryHookResult,
  'startPolling' | 'stopPolling'
>;

type JobDetailsQueryResult = JobDetailsQueryControls &
  (
    | {status: 'idle'; job: null; loading: false; error: undefined}
    | {status: 'loading'; job: null; loading: true; error: undefined}
    | {
        status: 'error';
        job: null;
        loading: false;
        error: NonNullable<FetchJobDetailsQueryHookResult['error']>;
      }
    | {status: 'empty'; job: null; loading: false; error: undefined}
    | {status: 'success'; job: JobDetails; loading: false; error: undefined}
  );

// Get a list of unique top-level config keys from a list of runs.
const getConfigKeys = (runs: JobRuns): string[] => {
  const configKeys: string[] = [];
  for (const run of runs) {
    for (const key in run.config) {
      if (!key.startsWith('_') && !configKeys.includes(key)) {
        configKeys.push(key);
      }
    }
  }
  return configKeys;
};

const useJobDetailsQueryResult = (
  jobId: string,
  {
    defaultJobVersion,
    usedByFilters,
    usedByFirst,
    includeRunArgs,
    pollInterval,
  }: JobDetailsQueryOptions
): JobDetailsQueryResult => {
  const {data, loading, error, startPolling, stopPolling} =
    useFetchJobDetailsQuery({
      variables: {
        jobId,
        usedByFilters,
        usedByFirst,
        includeRunArgs,
      },
      pollInterval,
      skip: !jobId,
    });

  if (loading) {
    return {
      status: 'loading',
      job: null,
      loading: true,
      error: undefined,
      startPolling,
      stopPolling,
    };
  }

  if (error) {
    return {
      status: 'error',
      job: null,
      loading: false,
      error,
      startPolling,
      stopPolling,
    };
  }

  if (!data) {
    return {
      status: 'idle',
      job: null,
      loading: false,
      error: undefined,
      startPolling,
      stopPolling,
    };
  }

  const job = getJobDetails(
    data.artifactCollection as ArtifactSequence | undefined,
    defaultJobVersion
  );

  if (!job) {
    return {
      status: 'empty',
      job: null,
      loading: false,
      error: undefined,
      startPolling,
      stopPolling,
    };
  }

  return {
    status: 'success',
    job,
    loading: false,
    error: undefined,
    startPolling,
    stopPolling,
  };
};

export const useJobDetailsNoPoll = (
  jobId: string,
  defaultJobVersion?: string
) =>
  useJobDetailsQueryResult(jobId, {
    defaultJobVersion,
    includeRunArgs: true,
  });

export const useJobDetails = (
  jobId: string,
  defaultJobVersion?: string,
  usedByFilters?: string,
  usedByFirst?: number
) =>
  useJobDetailsQueryResult(jobId, {
    defaultJobVersion,
    usedByFilters,
    usedByFirst,
    includeRunArgs: true,
    pollInterval: JOB_DETAILS_POLL_INTERVAL_MS,
  });

export const hasActiveJobRuns = (
  runs: ReadonlyArray<Pick<JobRun, 'state'>>
): boolean => runs.some(run => ACTIVE_STATES.includes(run.state));

export const useJobPageDetails = (jobId: string) => {
  const result = useJobDetailsQueryResult(jobId, {
    includeRunArgs: false,
  });
  const {startPolling, stopPolling} = result;
  const shouldPoll =
    result.status === 'success' && hasActiveJobRuns(result.job.runs);

  useEffect(() => {
    if (shouldPoll) {
      startPolling(JOB_DETAILS_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }

    return stopPolling;
  }, [jobId, shouldPoll, startPolling, stopPolling]);

  return result;
};

export const getJobDetails = (
  ac?: ArtifactCollection | ArtifactSequence,
  defaultJobVersion?: string
) => {
  if (ac == null) {
    return null;
  }
  const name = ac.name;
  const description = ac.description ?? '';
  const projectName = ac.project?.name ?? '';
  const entityName = ac.project?.entity?.name ?? '';
  const projectAccess = (ac.project?.access ?? 'PRIVATE') as AccessOptions;
  const createdAt = ac.createdAt.toString();

  const artifactEdges = ac.artifacts?.edges ?? [];
  const artifacts = artifactEdges.map(edge => edge.node as Artifact);

  const versions = artifacts.map(artifact =>
    convertArtifactToVersion(artifact)
  );

  // Get information such as run target.
  const artifactInfo = getArtifactInfo(artifactEdges as any);
  const versionInfo = getVersionInfo(artifactInfo, defaultJobVersion);

  // Aggregate run information from all versions of the job and sort.
  const aggregatedRuns: JobRuns = [];
  artifacts.forEach(artifact => aggregatedRuns.push(...extractRuns(artifact)));
  const runs = _.orderBy(aggregatedRuns, ['createdAt'], ['desc']);
  const configKeys = getConfigKeys(runs);

  const job: JobDetails = {
    id: ac.id,
    name,
    projectName,
    entityName,
    description,
    createdAt,
    projectAccess,
    versions,
    versionInfo,
    runs,
    configKeys,
  };

  return job;
};
